import { useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usePublicProfile } from '@/features/profile/hooks/usePublicProfile';
import ProfileHighlights from '@/features/profile/components/ProfileHighlights';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { EmptyState } from '@/components/ui/EmptyState';
import { useI18n } from '@/contexts/I18nContext';

const UserProfile = () => {
  const { username } = useParams<{ username: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();

  const loggedUsername = user?.user_metadata?.username;

  const { profile, loading, error } = usePublicProfile(username);

  // Estado da busca anterior (para preservar no retorno)
  const searchState = location.state?.searchState;
  const isFromSearch = !!searchState;
  const isFromHome = !!location.state?.fromHome;

  useEffect(() => {
    if (loggedUsername && username && loggedUsername === username) {
      navigate('/profile', { replace: true });
    }
  }, [loggedUsername, username, navigate]);

  useEffect(() => {
    if (error) {
      navigate('/', { replace: true });
    }
  }, [error, navigate]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    if (searchState) {
      // Retornar para a busca com estado preservado
      navigate('/search', { 
        state: { 
          searchState,
          preserveSearch: true 
        } 
      });
    } else if (isFromHome) {
      // Retornar para a home
      navigate('/home');
    } else {
      // Retornar para a página anterior ou home
      navigate(-1);
    }
  };

  if (loggedUsername && username && loggedUsername === username) {
    // Redirecting...
    return null;
  }

  if (loading) {
    return (
      <>
        <TopNavigation />
        <div className="min-h-screen bg-[#160C0C] pt-20 pb-20" style={{ width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
          <div className="w-full px-4" style={{ width: '100%', maxWidth: '100vw', boxSizing: 'border-box' }}>
            <div className="max-w-2xl mx-auto">
              {/* Botão voltar - apenas se veio da busca ou da home */}
              {(isFromSearch || isFromHome) && (
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span className="text-sm font-medium">
                    {isFromSearch ? t('common.back_to_search') : t('common.back_to_home')}
                  </span>
                </button>
              )}
              
              {/* Loading skeleton padronizado */}
              <div className="rounded-xl py-3 mt-2 mb-4 space-y-6 animate-pulse">
                {/* Header: Avatar + Nome/Username (estrutura similar ao ProfileHighlights) */}
                <div className="flex flex-row gap-2 mb-4 items-start w-full">
                  <div className="flex gap-4 flex-1 min-w-0">
                    {/* Avatar skeleton com frame */}
                    <div className="relative w-[60px] h-[66px] flex-shrink-0 mt-0">
                      <div className="w-[60px] h-[66px] bg-[#3A2A2A] rounded border-2 border-[#281B1B]"></div>
                    </div>
                    
                    {/* Nome e username skeleton */}
                    <div className="flex flex-col justify-start min-w-0 flex-1">
                      <div className="flex flex-col min-w-0 space-y-2">
                        <div className="h-5 bg-[#3A2A2A] rounded w-40"></div>
                        <div className="h-3 bg-[#3A2A2A] rounded w-32"></div>
                      </div>
                      {/* Academia skeleton */}
                      <div className="mt-1">
                        <div className="h-3 bg-[#3A2A2A] rounded w-36"></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Profile Totals Slider skeleton */}
                <div className="bg-[#231A1A] rounded-2xl p-4 border border-[#3A2A2A]">
                  <div className="flex items-center justify-between">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="flex flex-col items-center space-y-2">
                        <div className="h-8 bg-[#3A2A2A] rounded w-12"></div>
                        <div className="h-3 bg-[#3A2A2A] rounded w-16"></div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Connection button skeleton (se não for próprio perfil) */}
                <div className="h-10 bg-[#231A1A] rounded-full border border-[#3A2A2A]"></div>

                {/* Tabs skeleton */}
                <div className="flex gap-1 mt-6 mb-2 border-b border-[#281B1B] w-full">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex-1 pb-2">
                      <div className="h-5 bg-[#3A2A2A] rounded w-20 mx-auto"></div>
                    </div>
                  ))}
                </div>

                {/* Tab content skeleton - About Me */}
                <div className="space-y-4">
                  {/* Location skeleton */}
                  <div className="flex justify-between items-center">
                    <div className="h-4 bg-[#3A2A2A] rounded w-48"></div>
                    <div className="w-5 h-5 bg-[#3A2A2A] rounded"></div>
                  </div>

                  {/* Social links skeleton */}
                  <div className="flex flex-wrap gap-2">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-5 bg-[#3A2A2A] rounded w-24"></div>
                    ))}
                  </div>

                  {/* Profession skeleton */}
                  <div className="flex justify-between items-center">
                    <div className="h-4 bg-[#3A2A2A] rounded w-40"></div>
                    <div className="w-5 h-5 bg-[#3A2A2A] rounded"></div>
                  </div>

                  {/* Interests skeleton */}
                  <div className="space-y-2">
                    <div className="h-4 bg-[#3A2A2A] rounded w-32"></div>
                    <div className="flex flex-wrap gap-2">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="h-6 bg-[#3A2A2A] rounded-full w-20"></div>
                      ))}
                    </div>
                  </div>

                  {/* Favorite techniques skeleton */}
                  <div className="space-y-3">
                    <div className="h-4 bg-[#3A2A2A] rounded w-40"></div>
                    <div className="space-y-3">
                      {[1, 2].map((category) => (
                        <div key={category} className="space-y-2">
                          <div className="h-3 bg-[#3A2A2A] rounded w-28"></div>
                          <div className="flex flex-wrap gap-2">
                            {[1, 2, 3, 4].map((i) => (
                              <div key={i} className="h-6 bg-[#3A2A2A] rounded-full w-24"></div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <BottomNavigation />
      </>
    );
  }
  
  if (error) {
    return (
      <>
        <TopNavigation />
        <div className="min-h-screen bg-[#160C0C] pt-20 pb-20" style={{ width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
          <div className="w-full px-4" style={{ width: '100%', maxWidth: '100vw', boxSizing: 'border-box' }}>
            <div className="max-w-2xl mx-auto">
              {/* Botão voltar - apenas se veio da busca ou da home */}
              {(isFromSearch || isFromHome) && (
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span className="text-sm font-medium">
                    {isFromSearch ? t('common.back_to_search') : t('common.back_to_home')}
                  </span>
                </button>
              )}
              
              <EmptyState
                icon={AlertCircle}
                title={t('profile.not_found')}
                subtitle={t('profile.not_found_description')}
                className="text-white"
              />
            </div>
          </div>
        </div>
        <BottomNavigation />
      </>
    );
  }
  
  if (!profile) {
    return (
      <>
        <TopNavigation />
        <div className="min-h-screen bg-[#160C0C] pt-20 pb-20" style={{ width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
          <div className="w-full px-4" style={{ width: '100%', maxWidth: '100vw', boxSizing: 'border-box' }}>
            <div className="max-w-2xl mx-auto">
              {/* Botão voltar - apenas se veio da busca ou da home */}
              {(isFromSearch || isFromHome) && (
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span className="text-sm font-medium">
                    {isFromSearch ? t('common.back_to_search') : t('common.back_to_home')}
                  </span>
                </button>
              )}
              
              <EmptyState
                icon={AlertCircle}
                title={t('profile.not_found')}
                subtitle={t('profile.not_found_description')}
                className="text-white"
              />
            </div>
          </div>
        </div>
        <BottomNavigation />
      </>
    );
  }

  return (
    <>
      <TopNavigation />
      <div className="min-h-screen w-full bg-[#160C0C] pt-20 pb-20" style={{ width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
        {/* Botão voltar - apenas se veio da busca ou da home */}
        {(isFromSearch || isFromHome) && (
          <div className="px-4 mb-4" style={{ width: '100%', maxWidth: '100vw', boxSizing: 'border-box' }}>
            <div className="max-w-2xl mx-auto">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-white/60 hover:text-white mb-4 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="text-sm font-medium">
                  {isFromSearch ? t('common.back_to_search') : t('common.back_to_home')}
                </span>
              </button>
            </div>
          </div>
        )}
        
        <div className="w-full px-4" style={{ width: '100%', maxWidth: '100vw', boxSizing: 'border-box' }}>
          <div className="max-w-2xl mx-auto">
            <ProfileHighlights
              fullName={profile.full_name || ''}
              username={profile.username || ''}
              belt={profile.belt_rank || ''}
              beltColor={profile.belt_color || '#fff'}
              academies={profile.academies || []}
              location={{ country: profile.country, state: profile.state, city: profile.city }}
              social={{ instagram: profile.instagram, x: profile.x, facebook: profile.facebook, youtube: profile.youtube }}
              website={profile.website}
              interests={profile.interests || []}
              profession={profile.profession || ''}
              favoriteTechniques={profile.favorite_techniques || {}}
              techniqueCategories={profile.technique_categories || []}
              businessType={profile.business_type}
              linkedin={profile.linkedin}
              profilePictureUrl={profile.profile_picture_url}
              editable={false}
              isOwnProfile={false}
              userId={profile.id}
              isInstructor={profile.is_instructor || false}
              isPremium={profile.is_premium || false}
            />
          </div>
        </div>
      </div>
      <BottomNavigation />
    </>
  );
};

export default UserProfile; 